import { Grid } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AutocompleteDropdown } from 'src/shared/components/form/autocompleteDropdown';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { Association } from 'src/shared/models/association/association';
import { SelectableItem } from 'src/shared/models/gui/selectableItem';
import { getAssociations } from 'src/shared/services/associationService';

type UserEditAssociationDropdownProps = {
  setShowAssociationDropdown: (showDropdown: boolean) => void;
};

export const UserEditAssociationDropdown = (props: UserEditAssociationDropdownProps) => {
  const { setShowAssociationDropdown } = props;
  const { t } = useTranslation(['user']);
  const isMounted = useIsMounted();
  const { setValue } = useFormContext();

  const [associationData, setAssociationData] = useState<Association[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchAssociation = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getAssociations();
      if (isMounted()) {
        setAssociationData(result.data.results);
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [isMounted]);

  useEffect(() => {
    fetchAssociation();
  }, [fetchAssociation]);

  const handleSelectAssociationItem = async (val: SelectableItem | null) => {
    if (associationData && val !== null) {
      const selectedAssociation = associationData.find((item) => val.value === item.localAss_id.toString());
      if (selectedAssociation) {
        setValue('associations', [selectedAssociation]);
        setValue('districts', []);
        setShowAssociationDropdown(false);
      }
    }
  };

  return (
    <Grid container>
      <Grid item xs>
        <AutocompleteDropdown<SelectableItem>
          emptyLabel={t('user:selectAssociation')}
          placeholder={t('user:selectAssociation')}
          loading={loading}
          options={(associationData ?? []).map((x) => ({
            label: x.localAss,
            value: x.localAss_id.toString(),
          }))}
          nameProp='label'
          keyProp='value'
          name='associations'
          callback={(val) => handleSelectAssociationItem(val)}
          label={t('user:association')}
          componentWidth='100%'
        />
      </Grid>
    </Grid>
  );
};
